import { AbsoluteFill, interpolate, useCurrentFrame } from "remotion";
import {
  Caption,
  Callout,
  CodePanel,
  COLORS,
  FONTS,
  Panel,
  PipelineGraph,
  SceneTransition,
  SlideTimeBar,
  type PipeNode,
} from "../../coldcompute";
import { SCENE } from "./constants";
import {
  HOOK,
  PROBLEM,
  INTUITION,
  WHATIS,
  FLOW,
  HOOKIN,
  CODE,
  HONESTY,
  OUTRO,
} from "./script";

const center: React.CSSProperties = {
  alignItems: "center",
  justifyContent: "center",
  flexDirection: "column",
};

const Kicker: React.FC<{ text: string; color: string }> = ({ text, color }) => (
  <div
    style={{
      fontFamily: FONTS.mono,
      fontSize: 22,
      letterSpacing: "0.22em",
      textTransform: "uppercase",
      color,
      marginBottom: 22,
    }}
  >
    ▍{text}
  </div>
);

const Big: React.FC<{ children: React.ReactNode; size?: number }> = ({
  children,
  size = 92,
}) => (
  <div
    style={{
      fontFamily: FONTS.display,
      fontWeight: 700,
      fontSize: size,
      lineHeight: 0.98,
      letterSpacing: "-0.02em",
      color: COLORS.text,
      textAlign: "center",
      maxWidth: 1560,
    }}
  >
    {children}
  </div>
);

/** Persistent honesty pill — A2UI is NOT shipped in EAKC. Pinned top-right under the time bar. */
const RoadmapTag: React.FC = () => (
  <div
    style={{
      position: "absolute",
      top: 30,
      right: 40,
      display: "flex",
      alignItems: "center",
      gap: 10,
      fontFamily: FONTS.mono,
      fontSize: 16,
      letterSpacing: "0.16em",
      textTransform: "uppercase",
      color: COLORS.amber,
      border: `1px solid ${COLORS.amber}66`,
      background: "rgba(11,13,18,0.72)",
      borderRadius: 8,
      padding: "8px 14px",
    }}
  >
    <span
      style={{
        width: 9,
        height: 9,
        borderRadius: "50%",
        background: COLORS.amber,
        boxShadow: `0 0 10px ${COLORS.amber}`,
      }}
    />
    Roadmap · not yet shipped
  </div>
);

/* 01 — HOOK */
export const Scene01Hook: React.FC = () => {
  const frame = useCurrentFrame();
  const swap = interpolate(frame, [42, 70], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.hook.duration} />
      <SceneTransition from="up">
        <AbsoluteFill style={center}>
          <Kicker text="generative ui · forward-looking" color={COLORS.cyan} />
          <Big>
            Your AI stops sending{" "}
            <span style={{ color: COLORS.dim, opacity: 1 - swap * 0.6 }}>text</span>
            <br />
            and starts sending{" "}
            <span style={{ color: COLORS.cyan, opacity: 0.35 + swap * 0.65 }}>
              UI
            </span>
          </Big>
        </AbsoluteFill>
      </SceneTransition>
      <Caption lines={HOOK} accent="cyan" />
    </AbsoluteFill>
  );
};

/* 02 — PROBLEM */
export const Scene02Problem: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.problem.duration} />
    <RoadmapTag />
    <SceneTransition from="up">
      <AbsoluteFill style={center}>
        <Kicker text="the dead-output problem" color={COLORS.cyan} />
        <div style={{ display: "flex", gap: 28, alignItems: "stretch" }}>
          <Panel accent="cyan" title="today · markdown wall" width={500}>
            <div
              style={{
                fontFamily: FONTS.mono,
                fontSize: 21,
                lineHeight: 1.85,
                color: COLORS.dim,
              }}
            >
              <div># Competitor scan</div>
              <div>- Glean — dashboard builder</div>
              <div>- Onyx — static artifacts</div>
              <div style={{ color: COLORS.red }}>↳ can't sort · can't edit · can't approve</div>
            </div>
          </Panel>
          <Panel accent="red" title="the naive fix · unsafe" width={500}>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                height: 154,
                fontFamily: FONTS.mono,
                fontSize: 22,
                lineHeight: 1.7,
                color: COLORS.text,
              }}
            >
              <div style={{ color: COLORS.red }}>let LLM emit raw HTML / JS</div>
              <div style={{ color: COLORS.dim, fontSize: 18, marginTop: 10 }}>
                = arbitrary code in your UI
              </div>
              <div style={{ color: COLORS.red, fontSize: 18, marginTop: 6 }}>
                ✕ never do this
              </div>
            </div>
          </Panel>
        </div>
      </AbsoluteFill>
    </SceneTransition>
    <Caption lines={PROBLEM} accent="cyan" />
  </AbsoluteFill>
);

/* 03 — INTUITION */
export const Scene03Intuition: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.intuition.duration} />
    <RoadmapTag />
    <SceneTransition from="up">
      <AbsoluteFill style={center}>
        <Kicker text="the spectrum · static → declarative → generative" color={COLORS.violet} />
        <div style={{ display: "flex", gap: 18, alignItems: "stretch" }}>
          {[
            { name: "STATIC", sub: "fill dev-built components", color: COLORS.dim, dim: 0.55 },
            { name: "DECLARATIVE", sub: "ship a description · A2UI", color: COLORS.green, dim: 1 },
            { name: "GENERATIVE", sub: "write raw code · unsafe", color: COLORS.red, dim: 0.55 },
          ].map((t) => (
            <div
              key={t.name}
              style={{
                width: 320,
                opacity: t.dim,
                border: `1px solid ${t.color}${t.dim === 1 ? "" : "44"}`,
                background: t.dim === 1 ? "rgba(43,255,136,0.08)" : "rgba(18,21,29,0.6)",
                borderRadius: 12,
                padding: "20px 22px",
                textAlign: "center",
              }}
            >
              <div style={{ fontFamily: FONTS.display, fontWeight: 700, fontSize: 34, color: t.color }}>
                {t.name}
              </div>
              <div style={{ fontFamily: FONTS.mono, fontSize: 18, color: COLORS.dim, marginTop: 8 }}>
                {t.sub}
              </div>
            </div>
          ))}
        </div>
        <div
          style={{
            fontFamily: FONTS.body,
            fontSize: 30,
            color: COLORS.dim,
            marginTop: 32,
            textAlign: "center",
            maxWidth: 1200,
          }}
        >
          the agent names components from a catalogue your app already trusts —{" "}
          <span style={{ color: COLORS.green }}>safe like data, expressive like code</span>
        </div>
      </AbsoluteFill>
    </SceneTransition>
    <Caption lines={INTUITION} accent="violet" />
  </AbsoluteFill>
);

/* 04 — WHAT A2UI IS */
export const Scene04WhatIs: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.whatis.duration} />
    <RoadmapTag />
    <SceneTransition from="up">
      <AbsoluteFill style={center}>
        <Kicker text="what A2UI is · external · google" color={COLORS.green} />
        <Big size={120}>A2UI</Big>
        <div
          style={{
            fontFamily: FONTS.body,
            fontSize: 28,
            color: COLORS.dim,
            marginTop: 14,
            marginBottom: 36,
          }}
        >
          open declarative protocol · JSONL streaming · catalogue-based
        </div>
        <div style={{ display: "flex", gap: 18 }}>
          <Callout label="A2UI · Google" accent="green" width={340}>
            <b style={{ color: COLORS.text }}>the UI spec.</b> JSON components &amp;
            bindings — <span style={{ color: COLORS.green }}>no executable code</span>.
          </Callout>
          <Callout label="AG-UI · CopilotKit" accent="cyan" width={340}>
            the <b style={{ color: COLORS.text }}>transport</b> that carries it. A pipe,
            not a UI spec.
          </Callout>
          <Callout label="MCP apps · delivery" accent="amber" width={340}>
            the <b style={{ color: COLORS.text }}>sandbox</b>. iframe-isolated by default —
            Anthropic uses it for first-party UI too.
          </Callout>
        </div>
      </AbsoluteFill>
    </SceneTransition>
    <Caption lines={WHATIS} accent="green" />
  </AbsoluteFill>
);

/* 05 — FLOW */
const FLOW_PIPE: PipeNode[] = [
  { label: "agent", value: "JSON UI", sub: "streams JSONL", accent: "cyan" },
  { label: "client", value: "RENDER", sub: "own React comps", accent: "green" },
  { label: "user", value: "INTERACT", sub: "click · edit · sort", accent: "lime" },
  { label: "events", value: "→ AGENT", sub: "incremental update", accent: "violet" },
];
export const Scene05Flow: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.flow.duration} />
    <RoadmapTag />
    <AbsoluteFill style={{ ...center, padding: "0 90px" }}>
      <Kicker text="the loop · time-to-first-UI" color={COLORS.cyan} />
      <div style={{ width: 1680 }}>
        <PipelineGraph nodes={FLOW_PIPE} startFrame={8} stepFrames={24} nodeWidth={230} />
      </div>
      <div
        style={{
          fontFamily: FONTS.mono,
          fontSize: 20,
          color: COLORS.dim,
          marginTop: 34,
          letterSpacing: "0.06em",
        }}
      >
        components render progressively as the JSON streams in — bidirectional in A2UI v0.9
      </div>
    </AbsoluteFill>
    <Caption lines={FLOW} accent="cyan" />
  </AbsoluteFill>
);

/* 06 — WHERE I'D HOOK IT INTO EAKC */
export const Scene06HookIn: React.FC = () => {
  const frame = useCurrentFrame();
  const a = interpolate(frame, [10, 40], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const b = interpolate(frame, [200, 230], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.hookin.duration} />
      <RoadmapTag />
      <AbsoluteFill style={center}>
        <Kicker text="two real EAKC seams I'd wire it into" color={COLORS.lime} />
        <div style={{ display: "flex", gap: 28, alignItems: "stretch" }}>
          <div style={{ opacity: a }}>
            <Panel accent="lime" title="seam 1 · mission output" width={520}>
              <div style={{ fontFamily: FONTS.body, fontSize: 22, lineHeight: 1.5, color: COLORS.text }}>
                <div style={{ fontFamily: FONTS.mono, fontSize: 18, color: COLORS.dim }}>
                  generate_artifact.ts (shipped)
                </div>
                <div style={{ marginTop: 10 }}>
                  today: <b style={{ color: COLORS.text }}>PDF · DOCX · PPTX</b>
                </div>
                <div style={{ marginTop: 6, color: COLORS.lime }}>
                  + A2UI: a sortable comparison matrix, not a flat report
                </div>
              </div>
            </Panel>
          </div>
          <div style={{ opacity: b }}>
            <Panel accent="violet" title="seam 2 · approve / reject" width={520}>
              <div style={{ fontFamily: FONTS.body, fontSize: 22, lineHeight: 1.5, color: COLORS.text }}>
                <div style={{ fontFamily: FONTS.mono, fontSize: 18, color: COLORS.dim }}>
                  tool-grants route (shipped, mig 163)
                </div>
                <div style={{ marginTop: 10 }}>
                  today: <b style={{ color: COLORS.text }}>hand-built form</b>
                </div>
                <div style={{ marginTop: 6, color: COLORS.violet }}>
                  + A2UI: the agent generates the exact field set per action
                </div>
              </div>
            </Panel>
          </div>
        </div>
      </AbsoluteFill>
      <Caption lines={HOOKIN} accent="lime" />
    </AbsoluteFill>
  );
};

/* 07 — CODE (SKETCH, not shipped) */
const A2UI_JSON = `// SKETCH — how I'd shape an A2UI payload (NOT in EAKC yet)
{ "type": "Card", "title": "Competitor scan", "children": [
  { "type": "Table", "sortable": true,
    "columns": ["tool", "agent-driven UI?"],
    "rows": [["Glean","no"], ["Onyx","no"]] },
  { "type": "Button", "label": "Email summary",
    // routes back through the EXISTING trust gate
    "action": { "tool": "gmail-send", "requiresApproval": true } }
] }
// client maps "Table" -> <SortableTable/>, "Button" -> <Button/>
// the agent never emits HTML, never touches the DOM`;
export const Scene07Code: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.code.duration} />
    <RoadmapTag />
    <AbsoluteFill style={{ alignItems: "center", justifyContent: "center", paddingBottom: 40 }}>
      <CodePanel
        code={A2UI_JSON}
        language="json"
        filename="a2ui-payload.sketch.json"
        highlightLines={[7, 8, 9]}
        revealFrames={300}
        fontSize={23}
        width={1280}
      />
    </AbsoluteFill>
    <Caption lines={CODE} accent="cyan" />
  </AbsoluteFill>
);

/* 08 — HONESTY / STATUS */
export const Scene08Honesty: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.honesty.duration} />
    <SceneTransition from="up">
      <AbsoluteFill style={center}>
        <Kicker text="status check · no spin" color={COLORS.amber} />
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 18,
            border: `1px solid ${COLORS.green}66`,
            background: "rgba(43,255,136,0.06)",
            borderRadius: 14,
            padding: "26px 40px",
          }}
        >
          <span
            style={{
              width: 16,
              height: 16,
              borderRadius: "50%",
              background: COLORS.green,
              boxShadow: `0 0 16px ${COLORS.green}`,
            }}
          />
          <Big size={58}>
            A2UI in EAKC: <span style={{ color: COLORS.green }}>FOUNDATION shipped</span> ·{" "}
            <span style={{ color: COLORS.amber }}>last mile queued</span>
          </Big>
        </div>
        <div style={{ display: "flex", gap: 22, marginTop: 36 }}>
          <Callout label="shipped · 2026-06-05" accent="green" width={360}>
            safe catalogue + <b style={{ color: COLORS.text }}>fail-closed validator</b> +
            renderer + 17 tests. tsc clean.
          </Callout>
          <Callout label="still roadmap · C4" accent="amber" width={360}>
            the agent <b style={{ color: COLORS.text }}>emitting</b> A2UI and the chat{" "}
            <b style={{ color: COLORS.text }}>rendering</b> it end to end.
          </Callout>
        </div>
      </AbsoluteFill>
    </SceneTransition>
    <Caption lines={HONESTY} accent="amber" />
  </AbsoluteFill>
);

/* 09 — OUTRO */
export const Scene09Outro: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.outro.duration} />
    <SceneTransition from="up">
      <AbsoluteFill style={center}>
        <Big size={66}>
          Render the answer.
          <br />
          Don&apos;t just describe it.
          <br />
          <span style={{ color: COLORS.cyan }}>That&apos;s where agent UI is going.</span>
        </Big>
        <div
          style={{
            fontFamily: FONTS.mono,
            fontSize: 24,
            letterSpacing: "0.24em",
            color: COLORS.dim,
            marginTop: 44,
          }}
        >
          EAKC · building in public
        </div>
      </AbsoluteFill>
    </SceneTransition>
    <Caption lines={OUTRO} accent="cyan" />
  </AbsoluteFill>
);

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
